import { Pagination } from '../../../common/models/pagination'

/*
|--------------------------------------------------------------------------
| Fetch every page
|--------------------------------------------------------------------------
|
| List endpoints page their results. When a view needs the whole set for a
| range, such as the schedule, this fetches the first page to learn how many
| there are, then the rest in parallel, and returns them as one list.
*/

export type Page<T> = { items: T[]; pagination: Pagination }

/** A guard against a runaway range; the schedule never asks for anything near this. */
const DEFAULT_MAX_PAGES = 50

export async function fetchAllPages<T>(
  fetchPage: (page: number) => Promise<Page<T>>,
  options: { maxPages?: number } = {},
): Promise<Page<T>> {
  const maxPages = options.maxPages ?? DEFAULT_MAX_PAGES

  const first = await fetchPage(1)
  const totalPages = Math.min(first.pagination.totalPages, maxPages)

  if (totalPages <= 1) {
    return first
  }

  const rest = await Promise.all(
    Array.from({ length: totalPages - 1 }, (_, i) => fetchPage(i + 2)),
  )

  const items = rest.reduce(
    (all, page) => all.concat(page.items),
    first.items.slice(),
  )

  return {
    items,
    pagination: {
      ...first.pagination,
      count: items.length,
      currentPage: 1,
      perPage: items.length,
      totalPages: 1,
    },
  }
}
